import { FunctionComponent, lazy, startTransition, Suspense, useEffect, useState } from 'react';
import { Burger, AgoraLogo, Notifications } from './components';
import { NavbarItem } from '.';
import { ResponsiveContainer } from '../ResponsiveContainer';
import { Row } from '../Row';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { FunctionSearchTrigger } from '@/features/function-search';

const loadMobileMenu = () =>
  import('./components/MobileMenu').then((module) => ({ default: module.MobileMenu }));
const MobileMenu = lazy(loadMobileMenu);

type NavbarProps = {
  onHome: string;
  mainItems?: NavbarItem[];
  dropdownItems?: NavbarItem[];
};

export const Navbar: FunctionComponent<NavbarProps> = ({ onHome, mainItems = [], dropdownItems = [] }) => {
  const isDesktop = useIsDesktop();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setShowMobileMenu(false);
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  return (
    <header
      // Como la maqueta: línea de sombra de 1px en vez de borde. El fondo queda casi opaco
      // (97%) en lugar del 88% de la maqueta: sin desenfoque (modo rendimiento de Frost), al
      // 88% lo que pasa por debajo al desplazar se leería a través.
      className="sticky top-0 left-0 right-0 z-sticky h-16 bg-[rgba(var(--f-card-rgb),0.97)] shadow-[0_1px_0_rgba(var(--f-ink-rgb),0.06)] lg:hidden"
    >
      {/* La barra superior conserva los 16 px de siempre (la propuesta no la mueve). */}
      <ResponsiveContainer className="px-4">
        <Row align="center" justify="space-between" className="w-full h-16">
          {/* Logo Section */}
          <div className="flex items-center">
            <AgoraLogo href={onHome} />
          </div>

          <Row align="center" gap={10}>
            <FunctionSearchTrigger variant="topbar" />
            <Notifications variant="topbar" />
            {/* This header is visible below lg; desktop navigation lives in Rail. */}
            <Burger
              openMobileMenu={() => startTransition(() => setShowMobileMenu(true))}
              closeMobileMenu={() => startTransition(() => setShowMobileMenu(false))}
              isOpen={showMobileMenu}
              onIntent={() => void loadMobileMenu()}
            />
            {!isDesktop && showMobileMenu && (
              <Suspense fallback={null}>
                <MobileMenu
                  showMobileMenu
                  topItems={mainItems}
                  bottomItems={dropdownItems}
                  handleItemClick={() => startTransition(() => setShowMobileMenu(false))}
                />
              </Suspense>
            )}
          </Row>
        </Row>
      </ResponsiveContainer>
    </header>
  );
};
